import React, { useMemo, useState } from 'react';
import { FilePlus2, Loader2, Store } from 'lucide-react';
import type { Ticket } from '../../types';
import { Badge, Button, Card } from '../../shared/ui';
import { NouveauDossierModal } from './NouveauDossierModal';
import { RECHERCHE_MIN, useRechercheClients, versClientChoisi, type ClientChoisi } from './rechercheClients';

/**
 * Écran Clients : sous la liste des clients qui ont des dossiers, les autres
 * clients de klygroupe.com qui répondent à la même recherche (décision du
 * 16/10/2026). « Créer un dossier pour ce client » ouvre la fenêtre « Nouveau
 * dossier » avec ce client déjà sélectionné ; son compte SAV n'est créé (ou
 * vérifié) qu'à la validation de cette fenêtre, jamais si elle est annulée.
 * `exclureIds` : comptes SAV déjà affichés au-dessus (pas de doublon).
 */
export const AutresClientsBoutique: React.FC<{
  recherche: string;
  exclureIds: string[];
  onDossierCree: (dossier: Ticket) => void;
}> = ({ recherche, exclureIds, onDossierCree }) => {
  const etat = useRechercheClients(recherche, true, false);
  const [clientChoisi, setClientChoisi] = useState<ClientChoisi | null>(null);

  const exclus = useMemo(() => new Set(exclureIds), [exclureIds]);
  const autres = etat.resultats.filter(r => !r.compteSavId || !exclus.has(r.compteSavId));

  if (recherche.trim().length < RECHERCHE_MIN) return null;

  return (
    <Card className="p-4 max-sm:p-3">
      <section aria-label="Autres clients de la boutique">
        <div className="flex items-center gap-2">
          <Store className="w-4 h-4 text-sapin-700" />
          <h2 className="text-sm font-bold text-encre">Autres clients de la boutique</h2>
          {etat.enCoursBoutique && <Loader2 className="w-4 h-4 animate-spin text-graphite" aria-label="Recherche dans klygroupe.com" />}
        </div>
        <p className="mt-0.5 text-xs text-graphite">Comptes klygroupe.com qui répondent à la recherche ; leur compte SAV est créé si besoin à la création du dossier.</p>

        {etat.boutiqueIndisponible ? (
          <p className="mt-3 text-xs text-graphite" role="status">{etat.boutiqueIndisponible}</p>
        ) : etat.enCoursBoutique && autres.length === 0 ? (
          <p className="mt-3 text-xs text-graphite">Recherche dans klygroupe.com…</p>
        ) : autres.length === 0 ? (
          <p className="mt-3 text-xs text-graphite">Aucun autre client de la boutique ne correspond à cette recherche.</p>
        ) : (
          <ul className="mt-3 divide-y divide-trait rounded-lg border border-trait" aria-label="Clients de la boutique">
            {autres.map(r => (
              <li key={r.cle} className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <p className="text-sm font-semibold text-encre break-words">{r.titre}</p>
                    {r.compteSavId
                      ? <Badge className="bg-sapin-100 text-sapin-700">Compte SAV</Badge>
                      : <Badge className="bg-brume text-graphite border border-trait">Boutique (compte SAV créé à la validation)</Badge>}
                  </div>
                  {r.personne && <p className="text-xs text-encre break-words">{r.personne}</p>}
                  {r.email && <p className="text-xs text-graphite break-all">{r.email}</p>}
                  <p className="text-xs text-graphite tnum">
                    {[r.prestashopId != null ? `Compte boutique n° ${r.prestashopId}` : null, r.customerCode ? `Code SAGE ${r.customerCode}` : null].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <Button variant="secondary" size="sm" onClick={() => setClientChoisi(versClientChoisi(r))} className="w-full sm:w-auto shrink-0">
                  <FilePlus2 className="w-4 h-4 mr-1" />
                  Créer un dossier pour ce client
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {clientChoisi && (
        <NouveauDossierModal
          clientInitial={clientChoisi}
          onClose={() => setClientChoisi(null)}
          onCree={(dossier) => { setClientChoisi(null); onDossierCree(dossier); }}
        />
      )}
    </Card>
  );
};

export default AutresClientsBoutique;
